import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { LogosHeader } from '../../components/LogosHeader';
import { Logger } from '../../utils/Logger';

interface SyncProgressSkeletonProps {
  /**
   * Current sync cursor date (latest log entry received)
   * Format: ISO date string (e.g., "2025-02-02T10:30:45.000Z")
   */
  syncCursorDate?: string | null;

  /**
   * Minimum (oldest) log entry date found in the database
   * Format: ISO date string
   *
   * Used to calculate progress percentage:
   * progress = (syncCursorDate - minLogDate) / (now - minLogDate) * 100
   */
  minLogEntryCreatedDate?: string | null;
}

/**
 * Skeleton component displayed during logements synchronization
 *
 * Displays during SYNC_LOGEMENTS_PROGRESS state when:
 * - Initial app load is complete (INIT_STARTED done)
 * - Service Worker is syncing logements incrementally
 * - Progress data is available (syncCursorDate + minLogEntryCreatedDate)
 *
 * Features:
 * - Real logos (HandiToit, AG2R) - not skeleton
 * - "Synchronisation en cours..." text
 * - LinearProgress bar with calculated percentage
 *
 * Progress Calculation:
 * - 0% = minLogDate (oldest log in database)
 * - 100% = now (current time)
 * - current = (syncCursorDate - minLogDate) / (now - minLogDate) * 100
 *
 * Fallback:
 * - If dates are invalid or missing, show 0% progress bar
 * - This provides visual feedback even if calculation fails
 *
 * Note: Only used during sync of main app data (logements), not for account/settings pages
 */
export const SyncProgressSkeleton = ({
  syncCursorDate,
  minLogEntryCreatedDate
}: SyncProgressSkeletonProps) => {
  // Capture 'now' once at mount time (with useMemo) to prevent regression between batches
  // where re-renders would advance Date.now() while cursor stays constant, causing
  // progress = (cursor - min) / (now - min) to decrease slightly
  const nowDate = useMemo(() => Date.now(), []);

  // Calculate progress percentage based on dates
  let progressPercent = 0;

  if (syncCursorDate && minLogEntryCreatedDate) {
    try {
      const currentDate = new Date(syncCursorDate).getTime();
      const minDate = new Date(minLogEntryCreatedDate).getTime();

      // Only calculate if dates are valid and in correct order
      if (!isNaN(currentDate) && !isNaN(minDate) && minDate <= currentDate && currentDate <= nowDate) {
        progressPercent = Math.round(((currentDate - minDate) / (nowDate - minDate)) * 100);
      }
    } catch (error) {
      Logger.error('ProtectedPageSkeleton', 'Error calculating progress:', error);
      // Fall through to 0% on error
    }
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      textAlign: 'center',
      py: 2,
      px: 1
    }}>
      {/* Logos section */}
      <LogosHeader sx={{ mb: 2 }} />

      {/* Loading message */}
      <Stack spacing={2} sx={{ width: '100%', maxWidth: '400px', mt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Synchronisation en cours...
        </Typography>

        {/* Progress bar - show indeterminate when 0% to indicate work in progress */}
        <LinearProgress
          variant={progressPercent === 0 ? "indeterminate" : "determinate"}
          value={progressPercent}
          sx={{ height: 6, borderRadius: 3 }}
        />

        {/* Progress percentage text - hide when indeterminate */}
        {progressPercent > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {progressPercent}%
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
